"use client";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import {
  getCounts,
  getSearch,
  getList,
  getSearchWithList,
} from "./data-fetchers";
import { GridViewLayout, SearchX } from "@costar-search-x/components";
import {
  SearchMode,
  getNumberOfPages,
  getRecordSetSize,
  useLookupInfoProps,
  SearchViewMode,
} from "@costar-search-x/core";
import {
  getCompanyTypeAheadSuggestions,
  getLocationTypeAheadSuggestions,
  getPlaceDetail,
  lookupInfo,
} from "../shared/typeahead-data-fetchers";
import { BIThemeUIProvider } from "@/components/common/BIThemeUIProvider";
import { useLocationGridLayout } from "./LocationGridLayout";

import { SearchXLocationState } from "./types";
import "@costar/theme-ui/resources/styles/index.css";
import { getDetailUrl, getInitialCriteria } from "./constants";
import { DEFAULT_PAGESIZE } from "../shared/constants";
import {
  getCriteriaFromPage,
  getNumberOfItems,
  getRelativePageNumber,
} from "./search-helper";
import { getCriteriaParams, urlStateToCriteria } from "./criteria-url-helpers";
import {
  HiddenButtonsSearchPage,
  initialViewModeState,
} from "../shared/constants";
import { sort } from "./sort-helper";
import { useCanClearSearch } from "../shared/hooks/useCanClearSearch";
import { isCriteriaValid } from "./search-helper";
import { NoFilterPage } from "../shared/no-data/NoFilterPage";
import { SkeletonBar } from "../shared/SkeletonBar";
import { generatedResources } from "@/app/i18n/src";
import { MasterToolbarFiltersUi } from "./filters/master-toolbar-filters-ui";
import { useIsDesktop } from "../shared/hooks/useIsDesktop";
import { SideItemHelper } from "@/components/common/header/nav-bars/SideItemsHelper";
import { sitesLocationFilter } from "./filters/components/location-filter/filters";
import { FilterToolbarSearchIcon } from "../shared/FilterToolbarSearchIcon";
import { UserPreferences } from "@costar-suite/preferences-context";
import { SearchXErrorPage } from "@/components/common/error/error-page-searchx";
import { useURLCriteriaSync } from "../shared/hooks/use-url-criteria-sync";
import { secteurFilter } from "./filters/components/secteur-filter/filters";
import {
  getCountsNotAuth,
  getSearchWithListNotAuth,
} from "./unauth-data-fetchers";

export default function LocationSearchX({
  companyKey,
  companyName,
  isAuthenticated,
  userPreferences,
}: {
  companyKey: string;
  companyName: string;
  isAuthenticated?: boolean;
  userPreferences: UserPreferences;
}) {
  const messages = generatedResources["fr-FR"];
  const [state, onSearchUpdated] = useState<SearchXLocationState | undefined>();

  const [expandToolbar, setExpandToolbar] = useState(false);
  const gridLayout = useLocationGridLayout(messages);

  const initialCriteria = useMemo(
    () => getInitialCriteria(companyKey),
    [companyKey]
  );

  const urlStateToCriteriaWithCompanyKey = useMemo(
    () => urlStateToCriteria(companyKey),
    [companyKey]
  );

  const gridLayoutWithNoAuth: GridViewLayout | undefined = useMemo(() => {
    if (isAuthenticated === false && gridLayout) {
      return {
        ...gridLayout,
        columns: gridLayout?.columns?.map((c) => ({
          ...c,
          render: SkeletonBar,
        })),
      };
    }
    return gridLayout;
  }, [gridLayout, isAuthenticated]);

  const viewModeState = useMemo(() => initialViewModeState, []);
  const isDesktop = useIsDesktop();
  useEffect(() => {
    if (isDesktop) setExpandToolbar(false);
  }, [isDesktop]);

  const detailUrl = useMemo(
    () => getDetailUrl(companyKey, companyName),
    [companyKey, companyName]
  );

  const searchXInitialState = useURLCriteriaSync({
    baseCriteria: initialCriteria,
    defaultCriteria: initialCriteria,
    searchXState: state,
    pathName: detailUrl,
    getCriteriaParams,
    convertUrlToCriteria: urlStateToCriteriaWithCompanyKey,
  });

  const initialState = useMemo(
    () =>
      searchXInitialState.current && {
        searchState: {
          search: {
            criteria: {
              recordSetSize: getRecordSetSize(DEFAULT_PAGESIZE),
              recordSetNumber: getNumberOfPages(
                searchXInitialState.current?.page,
                getRecordSetSize(DEFAULT_PAGESIZE) / DEFAULT_PAGESIZE
              ),
              searchMode: SearchMode.Criteria,
              criteria: searchXInitialState.current?.criteria,
            },
            pageNumber: searchXInitialState.current?.page,
          },
          pager: { pageSize: DEFAULT_PAGESIZE },
        },
      },
    []
  );

  const canClearSearch = useCanClearSearch(
    initialCriteria,
    isCriteriaValid //Should this be shared?
  );

  const noDataMsg = useMemo(() => {
    return messages?.noData.noSearchCriteria();
  }, [messages]);

  const search = useMemo(
    () =>
      gridLayoutWithNoAuth && {
        gridLayout: gridLayoutWithNoAuth,
        getCriteriaFromPage,
        getRelativePageNumber,
        getNumberOfItems,
        sort,
        filters: {
          sitesLocationFilter,
          secteurFilter,
        },
        dataFetchers: {
          search: getSearch,
          counts: isAuthenticated === false ? getCountsNotAuth : getCounts,
          data: {
            [SearchViewMode.ListView]: getList,
          },
          searchWithData: {
            [SearchViewMode.ListView]:
              isAuthenticated === false
                ? getSearchWithListNotAuth
                : getSearchWithList,
          },
        },
        messages: {
          noData: noDataMsg,
        },
      },
    [gridLayoutWithNoAuth, isAuthenticated, noDataMsg]
  );

  const onResetSearch = useCallback(() => {
    const searchState = {
      search: {
        criteria: {
          recordSetSize: getRecordSetSize(DEFAULT_PAGESIZE),
          recordSetNumber: getNumberOfPages(
            1,
            getRecordSetSize(DEFAULT_PAGESIZE) / DEFAULT_PAGESIZE
          ),
          searchMode: SearchMode.Criteria,
          criteria: initialCriteria,
        },
        pageNumber: 1,
      },
      pager: { pageSize: DEFAULT_PAGESIZE },
    };

    return searchState;
  }, [initialCriteria]);

  const lookupInfo_ = useLookupInfoProps(lookupInfo);

  const onFilterIconClick = useCallback(
    (e: any) => {
      e.stopPropagation();
      e.preventDefault();
      setExpandToolbar(!expandToolbar);
    },
    [expandToolbar]
  );

  const searchIcon = useMemo(
    () => (
      <FilterToolbarSearchIcon
        openFiltersMessage={messages?.openFilters}
        onFilterIconClick={onFilterIconClick}
        isAuthenticated={isAuthenticated}
      />
    ),
    [isAuthenticated, messages?.openFilters, onFilterIconClick]
  );

  const sideItemComponent = useMemo(() => {
    return isDesktop ? null : searchIcon;
  }, [isDesktop, searchIcon]);

  const masterToolbarFilters = useCallback(
    ({
      criteria,
      updateCriteria,
    }: {
      criteria: any;
      updateCriteria: any;
    }): ReactElement => {
      return (
        <MasterToolbarFiltersUi
          criteria={criteria}
          updateCriteria={updateCriteria}
          companyTypeaheadFetcher={getCompanyTypeAheadSuggestions}
          locationTypeaheadFetcher={getLocationTypeAheadSuggestions}
          fetchPlaceDetail={getPlaceDetail}
          isDesktop={isDesktop}
          expandToolbar={expandToolbar}
          onFilterIconClick={onFilterIconClick}
          onClearFiltersButton={state?.searchActions.clearCriteria}
          resources={messages}
          isAuthenticated={isAuthenticated}
        />
      );
    },
    [
      isDesktop,
      expandToolbar,
      onFilterIconClick,
      state?.searchActions.clearCriteria,
      messages,
      isAuthenticated,
    ]
  );

  const masterToolbar = useMemo(() => {
    return {
      filters: masterToolbarFilters,
      toggleButtonsOptions: {
        hideButtons: HiddenButtonsSearchPage,
      },
    };
  }, [masterToolbarFilters]);

  const customComponents = {
    noData: <NoFilterPage message={noDataMsg} />,
  };

  const errorMessages = useMemo(() => {
    return {
      tryANewSearch: messages.common.error500Msg.tryANewSearch(),
      heading: messages.common.error500Msg.heading(),
      info: messages.common.error500Msg.info(),
    };
  }, [messages]);

  const refreshUrl = useMemo(() => {
    const detailUrl = getDetailUrl(companyKey, companyName);
    return `${detailUrl}?s=17&sd=0`;
  }, [companyKey, companyName]);

  const renderErrorOnSearch = useCallback(
    //TODO: Fix the type here, need to update the @costar/search-x-core package version
    (error?: any) => (
      <SearchXErrorPage
        refreshUrl={refreshUrl}
        error={error}
        messages={errorMessages}
      />
    ),
    [errorMessages, refreshUrl]
  );
  //TODO: Fix the type here, need to update the @costar/search-x-core package version
  const detectErrors = { renderErrorOnSearch };

  return search && initialState ? (
    <div>
      <SideItemHelper>{sideItemComponent}</SideItemHelper>
      <BIThemeUIProvider>
        <SearchX
          viewModeState={viewModeState}
          userPreferences={userPreferences}
          initialState={initialState}
          search={search}
          lookupInfo={isAuthenticated ? lookupInfo_ : undefined}
          onSearchUpdated={onSearchUpdated}
          onClearSearch={onResetSearch}
          masterToolbar={masterToolbar}
          canClearSearch={canClearSearch}
          components={customComponents}
          disablePagination={!isAuthenticated}
          detectErrors={detectErrors}
        />
      </BIThemeUIProvider>
    </div>
  ) : null;
}
